import { useEffect, useRef, useState } from 'react'

// ── Scale-to-fit TV stage ─────────────────────────────────────────────────
// Shared wrapper for fixed-layout TV screens. The content is designed at a
// fixed designW×designH canvas (default 1920×1080) and the whole stage is
// uniformly scaled by s = min(w/designW, h/designH) and centered — so it fits
// ANY TV at 100% browser zoom with zero manual adjustment (no more zooming to
// 80%). `embedded` measures the parent container (the TV Monitor preview tile);
// otherwise it fills the window. This is the same mechanism the Course Scroll
// Map used inline; extracted here so every fixed-layout TV screen reuses it.
export default function TvStage({ children, embedded = false, designW = 1920, designH = 1080, background = '#000' }) {
  const [scale, setScale] = useState(1)
  const ref = useRef(null)

  useEffect(() => {
    const compute = () => {
      const el = ref.current
      const w = embedded && el ? el.clientWidth : window.innerWidth
      const h = embedded && el ? el.clientHeight : window.innerHeight
      if (w > 0 && h > 0) setScale(Math.min(w / designW, h / designH))
    }
    compute()
    window.addEventListener('resize', compute)
    let ro
    if (embedded && ref.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(compute)
      ro.observe(ref.current)
    }
    return () => { window.removeEventListener('resize', compute); if (ro) ro.disconnect() }
  }, [embedded, designW, designH])

  const rootStyle = embedded
    ? { position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background, display: 'flex', alignItems: 'center', justifyContent: 'center' }
    : { position: 'fixed', inset: 0, overflow: 'hidden', background, display: 'flex', alignItems: 'center', justifyContent: 'center' }

  return (
    <div ref={ref} style={rootStyle}>
      <div style={{
        position: 'relative', width: designW, height: designH, flexShrink: 0,
        transform: `scale(${scale})`, transformOrigin: 'center center', overflow: 'hidden',
      }}>
        {children}
      </div>
    </div>
  )
}
